import React, { useState, useEffect } from "react";
import { Button, CircularProgress, Grid, Typography, Stepper, Step, StepLabel, Alert } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { format } from "date-fns";
import FormGeneralCompany from "./Forms/FormGeneralCompany";
import FormLegalInfo from "./Forms/FormLegalInfo";
import FormPrincipalKyc from "./Forms/FormPrincipalKyc";
import { FormBeneficialOwners } from "./Forms/FormBeneficialOwners";
import { clearOnboardingApplicationFlags, getPmDetails, getUpdateOnboardingApplication, selectOnboardingApplicationState } from "./sagas/pmonboardingSlice";
import TermsDialog from "../../components/TermsDialog";
import { confirmationText, incompleteApplicationtext } from "../shared/util/helpers";
import { schemaValidationBeneficialOwnerDataType } from "../../utils/validation";
import { OnboardingStepType } from "@inosio/payments-sdk";
import FormCompanyBankAccount from "./Forms/FormCompanyBankAccount";
import { uploadDocuments } from "../../shared/sagas/document/documentSlice";

interface Props {}
const steps = ["Not Submitted", "unApproved"];
export enum openAccordions {
  legalInfo = "legalInfo",
  generalCompanyInfo = "generalCompanyInfo",
  companyBankAccount = "companyBankAccount",
}

export const useStyle = makeStyles(() => ({
  accordionStyle: {
    marginBottom: "5px",
    borderRadius: "24px !important",
    overflow: "hidden",
  },
  submitBtn: {
    color: "#fff !important",
    background: "rgba(57, 162, 219, 1) !important",
    borderRadius: "24px !important",
    padding: "9px 17px !important",
    height: "49px",
    '&.Mui-disabled': {
      background: "rgba(211, 211, 211, 1) !important",
    }
  },
  submitBtnText: {
    textTransform: "capitalize",
    fontSize: '18px !important',
    lineHeight: '22px !important',
    fontWeight: '700 !important',
    fontFamily: "Work Sans !important",
  },
  finishLaterBtnWrapper: {
    margin: "1px",
    marginBottom: "40px",
    position: "relative",
  },
  finishLaterBtn: {
    borderRadius: "24px !important", 
    border: "1px solid #39A2DB !important",
    padding: "9px 17px !important",
    height: "49px !important", 
  },
  finishLaterBtnText: {
    textTransform: "capitalize",
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#39A2DB !important',
  },
  inputStyle: {
    boxShadow: "none !important",
  },
  bgStyle: {
    background: "#fff",
    borderRadius: "24px !important",
    padding: "15px",
  },
  stepperStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "50%",
  },
  wrapper: {
    margin: "1px",
    marginLeft: "5%",
    position: "relative",
  },
  buttonProgress: {
    color: "#39A2DB",
    position: "absolute",
    top: "12px",
    left: "calc( 50% - 13px )",
  },
}));

export const ApplicationDashboard = (props: Props) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const history = useHistory();

  const { pmDetails, onboarding, errorMessage, isFetching, isSuccess } = useSelector(selectOnboardingApplicationState);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openFinishLaterConfirmation, setOpenFinishLaterConfirmation] = useState(false);
  const [showAccordionDetails, setShowAccordionDetails] = useState<openAccordions | false>(openAccordions.generalCompanyInfo);
  const [showBenOwnerForm, setShowBenOwnerForm] = useState(false);
  const [benOwnersCount, setBenOwnersCount] = useState(0);
  const [totalOwnership, setTotalOwnership] = useState(0);

  useEffect(() => {
    if (onboarding?.BeneficialOwners?.length) setBenOwnersCount(onboarding?.BeneficialOwners?.length);

    const ownership = (onboarding?.PrincipalOfficer?.OwnershipPercentage || 0) + (onboarding?.BeneficialOwners?.map((benOwner) => benOwner.OwnershipPercentage || 0).reduce((prev, next) => prev + next, 0) || 0);
    setTotalOwnership(ownership);
  }, [onboarding])

  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);
  const [addedFiles, setAddedFiles] = React.useState<any>([]);

  const handleClickOpencConfirmation = () => {
    dispatch({
      type: getUpdateOnboardingApplication.type,
      payload: {
        company_uuid: onboarding?.Uuid,
        data: { ...onboarding, CompletedOnboardingStep: OnboardingStepType.PaymentsProcessingApplication },
      },
    });
    setIsFormSubmitted(true);
    //we save bank files for further api call
    if (onboarding?.BankAccount?.BankFiles) setAddedFiles(onboarding.BankAccount.BankFiles.filter((f: any) => f.name));
  };

  const handleClickFinishLater = () => {
    dispatch({
      type: getUpdateOnboardingApplication.type,
      payload: {
        company_uuid: onboarding?.Uuid,
        data: onboarding, // finish later
      },
    });
    setIsFormSubmitted(true);
    //we save bank files for further document api call
    if (onboarding?.BankAccount?.BankFiles) setAddedFiles(onboarding.BankAccount.BankFiles.filter((f: any) => f.name));
  };

  React.useEffect(() => {
    if (isFormSubmitted && isSuccess && !isFetching) {
      if(onboarding.CompletedOnboardingStep !== OnboardingStepType.PaymentsProcessingApplication) setOpenFinishLaterConfirmation(true);

      if (onboarding?.BankAccount?.EntityUuid) {
        if (addedFiles.length) {
          dispatch({
            type: uploadDocuments.type,
            payload: {
              belongsToEntityUuid: onboarding.BankAccount.EntityUuid,
              documents: addedFiles,
              documentType: 'BankAccountFile',
            }
          });
  
          setAddedFiles([]);
        }
      }
    }
  }, [isFormSubmitted, isSuccess, isFetching, onboarding]);
  
  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
    history.push("/onboarding/wizard");
  };

  useEffect(() => {
    // Clear all onboarding flags
    dispatch({
      type: clearOnboardingApplicationFlags.type,
      payload: {},
    });

    // fetch company uuid if it doesnt exist in app context
    !onboarding?.Uuid &&
      dispatch({
        type: getPmDetails.type,
        payload: {},
      });
  }, []);

  useEffect(() => {
    // fetch onboarding application if it doesnt exist in app context
    pmDetails.Company?.Uuid &&
      !onboarding?.Uuid &&
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: pmDetails.Company.Uuid,
          data: {},
        },
      });
  }, [pmDetails]);

  // the only time OnboardingStepType  when we submit
  useEffect(() => {
    setOpenConfirmation(onboarding.CompletedOnboardingStep === OnboardingStepType.PaymentsProcessingApplication);
  }, [onboarding?.CompletedOnboardingStep]);

  const disableSubmit = () => {
    const { PrincipalOfficer, IncorporationState, TaxId, CompanyType, PhysicalAddress, MailingAddress, DbaName, LegalName, PhoneNumber, Email, BankAccount } = onboarding; // fecth every known key in updateCompanyOnboardingDto
    const validationPassed = [PrincipalOfficer?.FirstName, PhysicalAddress?.Country, MailingAddress?.Country, IncorporationState, TaxId, CompanyType, DbaName, LegalName, PhoneNumber, Email, BankAccount?.BankName].every((item) => !!item); // check that they have been populated

    return !validationPassed;
  };

  return (
    <Box pt={2} sx={{ background: "rgba(5, 55, 66, 1)", minHeight: "100vh", padding: "2%" }}>
      {errorMessage && errorMessage?.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}
      <Grid container display={"flex"} justifyContent="center" sx={{ "& .MuiGrid-item": { marginTop: "3%" } }}>
        <Grid item md={3}></Grid>
        <Grid item className={classes.stepperStyle} xs={12} md={6}>
          <Stepper alternativeLabel style={{ width: "65%" }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>
                  <Typography color="white">{label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>
        <Grid item display="flex" flexDirection={"row"} xs={12} justifyContent={"center"} md={3}>
          <div className={classes.finishLaterBtnWrapper}>
            <Button
              type="submit"
              variant="outlined"
              className={classes.finishLaterBtn}
              onClick={handleClickFinishLater}
            >
              <Typography className={classes.finishLaterBtnText}>{`Save & Finish Later`}</Typography>
            </Button>
          </div>
          <div className={classes.wrapper}>
            <Button 
              type="submit" 
              disabled={disableSubmit()} 
              className={classes.submitBtn}
              onClick={handleClickOpencConfirmation}
            >
              <Typography className={classes.submitBtnText}>Submit Application</Typography>
            </Button>
            {isFetching && <CircularProgress size={26} className={classes.buttonProgress} />}
          </div>
        </Grid>
      </Grid>

      <Grid container justifyContent={"center"} spacing={1} sx={{ "& .MuiGrid-item": { paddingTop: 0 }, marginTop: "3%" }}>
        <Grid item sm={4}>
          <FormGeneralCompany openAccordion={showAccordionDetails} setOpenAccordion={setShowAccordionDetails} />
          <FormLegalInfo openAccordion={showAccordionDetails} setOpenAccordion={setShowAccordionDetails} />
          <FormCompanyBankAccount openAccordion={showAccordionDetails} setOpenAccordion={setShowAccordionDetails} />
        </Grid>

        <Grid item sm={4}>
          <FormPrincipalKyc />
        </Grid>

        {onboarding?.PrincipalOfficer?.FirstName && (showBenOwnerForm || onboarding?.BeneficialOwners?.length) ? (
          <Grid item sm={4}>
            {onboarding?.PrincipalOfficer?.FirstName && (
              <>
                {!!onboarding?.BeneficialOwners?.length &&
                  onboarding?.BeneficialOwners?.map((beneficialOwner, index) => {
                    const benOwner = {
                      first_name: beneficialOwner.FirstName!,
                      last_name: beneficialOwner.LastName!,
                      middle_name: beneficialOwner.MiddleName || "",
                      ssn: beneficialOwner.Ssn!,
                      email: beneficialOwner.Email!,
                      phone: beneficialOwner.PhoneNumber!,
                      physical_address: beneficialOwner.PhysicalAddress?.Line1!,
                      city: beneficialOwner.PhysicalAddress?.City!,
                      state: beneficialOwner.PhysicalAddress?.State!,
                      zip: beneficialOwner.PhysicalAddress?.Zip!,
                      country: beneficialOwner.PhysicalAddress?.Country!,
                      ownership_amount: beneficialOwner.OwnershipPercentage!,
                      dob: beneficialOwner.Dob && format(new Date(beneficialOwner.Dob), "MM/dd/yyyy"),
                      passport_number: beneficialOwner.PassportIdNumber!,
                    };
                    return <FormBeneficialOwners index={index} benOwner={benOwner} key={`Ben${index}`} />;
                  })
                }

                {showBenOwnerForm && totalOwnership <= 75 && (
                  <FormBeneficialOwners openAccordion index={benOwnersCount} benOwner={{} as schemaValidationBeneficialOwnerDataType} setShowBenOwnerForm={setShowBenOwnerForm} />
                )}

                {!showBenOwnerForm && totalOwnership <= 75 &&  (
                  <Button
                    variant="outlined"
                    sx={{ width: "100%", borderRadius: "24px", marginTop: "3%", height: "49px" }}
                    onClick={() => {
                      setShowBenOwnerForm(true);
                    }}
                  >
                    Add Beneficial Owner
                  </Button>
                )}
              </>
            )}
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
      <TermsDialog open={openConfirmation} scroll={false} text={confirmationText} title="Inosio Payment Processing Application Received" handleConfirm={handleCloseConfirmation} buttonText="Continue" />
      <TermsDialog open={openFinishLaterConfirmation} scroll={true} text={incompleteApplicationtext} title="Inosio Payment Processing Application Received" handleConfirm={handleCloseConfirmation} buttonText="Continue" />
    </Box>
  );
};
